import classnames from "classnames";
import { differenceInCalendarDays } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { inject } from "mobx-react";
import { Fragment } from "react";
import type { Store } from "@inferno/renderer-shared-core";
import type { BlockFragment, CurrentlyPlayingTrackDetailFragment } from "@ihr-radioedit/inferno-webapi";
import { blockHaveAd } from "@inferno/renderer-shared-core";
import { MusicPageType } from "../../lib/catalog";
import { AdPosition } from "../Ad.component";
import { DateSeparator } from "./DateSeparator.component";
import { TAKE } from "./MusicPage.component";
import { MusicTrack } from "./MusicTrack.component";

interface RecentTrackListItemProps {
  track: CurrentlyPlayingTrackDetailFragment;
  lastTrack?: CurrentlyPlayingTrackDetailFragment;
  trackNum?: number;
  timezone?: string;
  currentTrackId: number;
}

const isNewDay = (date1: number, date2: number, timezone = "America/New_York") => {
  if (!timezone) {
    return false;
  }
  const utcDate1 = utcToZonedTime(date1, timezone);
  const utcDate2 = utcToZonedTime(date2, timezone);
  const daysBetween = differenceInCalendarDays(utcDate1, utcDate2);
  return daysBetween !== 0;
};

const RecentTrackListItem = ({
  trackNum = 0,
  track,
  lastTrack,
  timezone = "",
  currentTrackId,
}: RecentTrackListItemProps) => {
  let newDay = false;
  if (lastTrack) {
    const { startTime: trackStart } = track;
    const { startTime: lastTrackStart } = lastTrack;
    if (trackStart && lastTrackStart) {
      newDay = isNewDay(trackStart * 1000, lastTrackStart * 1000, timezone);
    }
  }

  const itemClass = classnames("track-list-item", {
    "date-separated": newDay,
  });

  return (
    <li className={itemClass}>
      {newDay ? <DateSeparator timestamp={track.startTime ? track.startTime * 1000 : 0} timezone={timezone} /> : null}
      <MusicTrack
        track={track}
        type={MusicPageType.RECENT}
        current={track.trackId === currentTrackId && trackNum === 0}
      />
    </li>
  );
};

export const RecentTrackList = inject("store")(
  ({
    data,
    store,
    currentTrackId,
    block,
  }: {
    data: CurrentlyPlayingTrackDetailFragment[];
    currentTrackId: number;
    store?: Store;
    block: BlockFragment;
  }) => {
    let timezone = store?.env.DEFAULT_TIMEZONE;
    if (store?.site.index.timeZone) {
      timezone = store.site.index.timeZone;
    }
    const adBlockData = { value: { ...block.value } } as BlockFragment;
    const showAd = blockHaveAd(block.value.position);
    // use index to get the previous item in the list to compare start times
    // but first need to check for 0 so no error is thrown
    return (
      <ul className="component-track-list playlist-recent">
        {data.map((track, index) => {
          return (
            <Fragment key={index}>
              {showAd && index > 0 && index % TAKE === 0 ? (
                <li className="load-more-ad">
                  <AdPosition
                    block={adBlockData}
                    batchId={`${track.albumId}-${Math.ceil(index / block.value.position)}`}
                  />
                </li>
              ) : null}
              {index > 0 ? (
                <RecentTrackListItem
                  key={index}
                  trackNum={index}
                  track={track}
                  lastTrack={data[index - 1]}
                  timezone={timezone}
                  currentTrackId={currentTrackId}
                />
              ) : (
                <RecentTrackListItem
                  key={index}
                  trackNum={index}
                  track={track}
                  timezone={timezone}
                  currentTrackId={currentTrackId}
                />
              )}
            </Fragment>
          );
        })}
      </ul>
    );
  },
);

export default RecentTrackList;
